/*
 * One picture, large (spec 07 §4 "Timeline", X-B1 D3).
 *
 * It is the shared `Dialog` in its `lightbox` variant, so the focus trap, the Escape and the return of
 * focus to whatever opened it are the primitive's and not a second hand-rolled version of them. What
 * this file adds is the walk through the pictures of one message: `←` and `→` anywhere in the dialog,
 * and buttons that do the same thing for a pointer and for a screen reader. The position ("2 of 4") is
 * on screen and in the dialog's own name, because a reader who cannot see the strip of thumbnails has
 * no other way to know where they are.
 *
 * A picture is looked at, not read, so the viewer is a room and not a panel: the overlay carries the
 * whole ground, the dialog has none, and everything that is not the picture stands in one bar at the
 * top in the rail's own ink — the file name, the position, and the four things one does with a picture.
 * The page is behind the scrim and not through it: a caption that was legible only because the message
 * under it happened to be pale is a caption that cannot be read (`attachments.css`).
 */

import { useEffect } from "react"
import { Icon } from "../../../core/shell/index.ts"
import type { AttachmentWire } from "../../../core/wire/index.ts"
import { Button, Dialog } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import { altOf } from "./AttachmentList.tsx"
import "./attachments.css"

export interface LightboxProps {
	files: readonly AttachmentWire[]
	index: number
	onIndex: (index: number) => void
	onClose: () => void
}

export function Lightbox({ files, index, onIndex, onClose }: LightboxProps) {
	const file = files[index]

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "ArrowLeft" && index > 0) onIndex(index - 1)
			if (event.key === "ArrowRight" && index < files.length - 1) onIndex(index + 1)
		}
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [index, files.length, onIndex])

	if (file === undefined) return null
	const position = t("chat.attachment.position", { position: index + 1, total: files.length })
	const name = altOf(file)

	return (
		<Dialog
			open
			variant="lightbox"
			onOpenChange={(open) => {
				if (!open) onClose()
			}}
			title={`${name} — ${position}`}
		>
			<div className="tb-lightbox">
				{/* The bar says what this is and offers what one does with it, the way out included: the
				    dialog draws no close of its own under this variant, because a viewer's way out belongs
				    in the row the reader is already reading. */}
				<div className="tb-lightbox__bar">
					{/* The file's own name, not its alt text: the caption is the sender's words and stands
					    under the picture, while the bar says which file is on screen. */}
					<span className="tb-lightbox__name">{file.filename}</span>
					{files.length === 1 ? null : <span className="tb-lightbox__position">{position}</span>}
					<div className="tb-lightbox__actions">
						{files.length === 1 ? null : (
							<>
								<Button
									variant="ghost"
									quiet
									icon
									className="tb-lightbox__step"
									aria-label={t("chat.attachment.previous")}
									before={<Icon name="chevronLeft" />}
									disabled={index === 0}
									onClick={() => onIndex(index - 1)}
								/>
								<Button
									variant="ghost"
									quiet
									icon
									className="tb-lightbox__step"
									aria-label={t("chat.attachment.next")}
									before={<Icon name="chevronRight" />}
									disabled={index === files.length - 1}
									onClick={() => onIndex(index + 1)}
								/>
							</>
						)}
						<Button
							variant="ghost"
							quiet
							icon
							className="tb-lightbox__step"
							href={file.url}
							download={file.filename}
							aria-label={t("chat.attachment.download")}
							before={<Icon name="download" />}
						/>
						<Button
							variant="ghost"
							quiet
							icon
							className="tb-lightbox__step"
							aria-label={t("chat.lightbox.close")}
							before={<Icon name="close" />}
							onClick={onClose}
						/>
					</div>
				</div>
				<figure className="tb-lightbox__figure">
					{/* The large derived variant, not the original: a 40-megapixel photograph would
					    otherwise be decoded in full to be looked at once. */}
					<img
						className="tb-lightbox__image"
						src={file.previewUrl !== "" ? file.previewUrl : file.url}
						alt={name}
					/>
					{file.caption === "" ? null : (
						<figcaption className="tb-lightbox__caption">{file.caption}</figcaption>
					)}
				</figure>
			</div>
		</Dialog>
	)
}
